/**
 * Schedule fetch, IndexedDB hydrate, and cache reload for /schedule/tasks.
 * Does not own save, discard, or the DHTMLX grid.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactGanttRef } from '@dhx/react-gantt';
import { notifications } from '@mantine/notifications';
import {
  dependencyTypeToDhtmlx,
  mapPlanningApiTaskRowToSnapshot,
} from '../../../utils/utils';
import { fetchLookAheadConfig } from '../../../api/lookAheadConfigApi';
import { fetchAllLinks, fetchAllTasks } from '../../../api/schedulingApis';
import {
  fallbackLookAheadConfig,
  type TargetsLookAheadConfig,
} from '../components/targets/targetsLookAheadConfig';
import type { ITaskStore } from '../../../stores/ITaskStore';
import type { LinkSnapshot, TaskSnapshot } from '../../../stores/types';
import { useLocationsTreeStore } from '../../../stores/locationsTreeStore';
import {
  isLikelyAutoSeedOnlySchedule,
  isPlanningShellTask,
} from '../../../gantt/addTaskUtils';
import { ensureEmptyGanttRootSchedule } from '../../../gantt/ensureEmptyGanttRoot';
import {
  consumeGanttForceRefetch,
  invalidateGanttProjectCaches,
} from '../../../gantt/ganttCacheLifecycle';
import {
  clearGanttSessionSnapshot,
  loadGanttSessionSnapshot,
} from '../../../gantt/ganttSessionSnapshot';
import { deferAfterPaint } from '../../../utils/deferAfterPaint';
import { markGanttPerf, measureGanttPerf } from '../../../gantt/ganttPerfGuards';

type ReloadReason =
  | 'initial'
  | 'invalidate-fetch'
  | 'discard'
  | 'server-recalc'
  | 'baseline-rewrite'
  | 'save-uncertain'
  | 'post-save';

export function useGanttScheduleLoad(params: {
  store: ITaskStore;
  ganttRef: React.RefObject<ReactGanttRef | null>;
  projectId: string;
  dismissGanttInlineEditing: () => void;
  canTaskCreate: boolean;
  projectCalendarId: string;
  defaultCalendarId: string;
  refreshActiveRevision: () => Promise<unknown>;
}) {
  const {
    store,
    ganttRef,
    projectId,
    dismissGanttInlineEditing,
    canTaskCreate,
    projectCalendarId,
    defaultCalendarId,
    refreshActiveRevision,
  } = params;

  const [lookAheadConfig, setLookAheadConfig] = useState<TargetsLookAheadConfig>(fallbackLookAheadConfig);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  /** Gate readiness popover until first schedule + location tree hydrate finishes. */
  const [readinessDataReady, setReadinessDataReady] = useState(false);
  /**
   * Once-per-project empty-schedule seed. Only set after the server confirms zero tasks —
   * never seed from an empty local/IDB store alone (that overwrote real schedules).
   */
  const ensureGanttRootAttemptedRef = useRef<string | null>(null);

  /**
   * Fetch tasks and links from API, transform, set store, persist to IDB.
   * Used on initial load (when hydrate returns false) and by reload policy helpers.
   */
  const fetchFromApiAndPersist = useCallback(async () => {
    if (!projectId) return;
    setLoadingProgress('Fetching schedule data from server');
    markGanttPerf('apiFetch');
    const [tasksResponse, linksResponse] = await Promise.all([
      fetchAllTasks({ projectId }),
      fetchAllLinks({ projectId }).catch(err => {
        console.error('[ZustandGantt] Failed to fetch links:', err);
        return { data: [] };
      })
    ]);
    measureGanttPerf('apiFetch');
    if (!tasksResponse?.data || !Array.isArray(tasksResponse.data)) {
      setLoadingProgress('');
      return;
    }
    if (tasksResponse.data.length === 0) {
      store.setTasks([]);
      store.setLinks([]);
      dismissGanttInlineEditing();
      // Seed only when the server confirmed an empty schedule — never from empty IDB alone.
      if (
        canTaskCreate &&
        ensureGanttRootAttemptedRef.current !== projectId
      ) {
        ensureGanttRootAttemptedRef.current = projectId;
        try {
          setLoadingProgress('Creating project root task...');
          const seedResult = await ensureEmptyGanttRootSchedule({
            projectId,
            store,
            calendarId: projectCalendarId || defaultCalendarId || undefined,
          });
          if (!seedResult.seeded) {
            // Precheck found server tasks (or aborted) — reload so we never keep an empty cache.
            ensureGanttRootAttemptedRef.current = projectId;
            const [retryTasks, retryLinks] = await Promise.all([
              fetchAllTasks({ projectId }),
              fetchAllLinks({ projectId }).catch(() => ({ data: [] })),
            ]);
            if (Array.isArray(retryTasks?.data) && retryTasks.data.length > 0) {
              const tasks: TaskSnapshot[] = retryTasks.data.map((t: any) =>
                mapPlanningApiTaskRowToSnapshot(t),
              );
              const links: LinkSnapshot[] = [];
              if (Array.isArray(retryLinks?.data)) {
                for (const link of retryLinks.data) {
                  links.push({
                    id: link.id,
                    source: link.source,
                    target: link.target,
                    type: dependencyTypeToDhtmlx(link.type),
                    lag: link.lag || null,
                  });
                }
              }
              store.setTasks(tasks);
              store.setLinks(links);
              await store.persist(projectId);
            } else {
              await store.persist(projectId);
            }
          }
        } catch (err) {
          console.warn('[Gantt] Failed to seed empty schedule root:', err);
          ensureGanttRootAttemptedRef.current = null;
          await store.persist(projectId);
        }
      } else {
        await store.persist(projectId);
      }
      setLoadingProgress('');
      return;
    }
    setLoadingProgress('Processing tasks and links...');
    markGanttPerf('transform');
    const tasks: TaskSnapshot[] = [];
    const links: LinkSnapshot[] = [];
    for (const t of tasksResponse.data) {
      tasks.push(mapPlanningApiTaskRowToSnapshot(t));
    }
    if (linksResponse?.data && Array.isArray(linksResponse.data)) {
      for (const link of linksResponse.data) {
        links.push({
          id: link.id,
          source: link.source,
          target: link.target,
          type: dependencyTypeToDhtmlx(link.type),
          lag: link.lag || null,
        });
      }
    }
    measureGanttPerf('transform');
    setLoadingProgress('Storing data...');
    markGanttPerf('setTasks');
    store.setTasks(tasks);
    store.setLinks(links);
    measureGanttPerf('setTasks');
    dismissGanttInlineEditing();
    setLoadingProgress('Persisting data locally for smooth experience');
    await store.persist(projectId);
    setLoadingProgress('');
    // Schedule orphan cleanup (same as initial load)
    setTimeout(() => {
      const gantt = ganttRef.current?.instance;
      if (!gantt) return;
      const ganttTasks = gantt.serialize().data;
      const ganttTaskIds = new Set(ganttTasks.map((t: any) => t.id));
      const storeTasks = store.getTasks();
      const visibleStoreCount = storeTasks.filter(
        (t) => !isPlanningShellTask(t, storeTasks),
      ).length;
      if (ganttTaskIds.size < visibleStoreCount) {
        const validTasks = storeTasks.filter(
          (t) => ganttTaskIds.has(t.id) || isPlanningShellTask(t, storeTasks),
        );
        const storeLinks = store.getLinks();
        const validLinks = storeLinks.filter(l => ganttTaskIds.has(l.source) && ganttTaskIds.has(l.target));
        store.setTasks(validTasks);
        store.setLinks(validLinks);
        store.persist(projectId);
      }
    }, 2000);
  }, [
    store,
    projectId,
    dismissGanttInlineEditing,
    canTaskCreate,
    projectCalendarId,
    defaultCalendarId,
    ganttRef,
  ]);

  /**
   * Central reload policy: always full refetch for discard / invalidate / server recalc /
   * baseline rewrite / uncertain save. Delta save skips this when local store is authoritative.
   */
  const reloadScheduleFromServer = useCallback(
    async (opts: {
      reason: ReloadReason;
      invalidateLocalCache?: boolean;
    }) => {
      if (!projectId) return;
      markGanttPerf('saveRefetch');
      if (opts.invalidateLocalCache) {
        await invalidateGanttProjectCaches(projectId);
      }
      await fetchFromApiAndPersist();
      measureGanttPerf('saveRefetch');
    },
    [projectId, fetchFromApiAndPersist],
  );

  /**
   * Invalidate IndexedDB cache for current project and refetch from API.
   */
  const handleInvalidateAndFetch = useCallback(async () => {
    if (!projectId) {
      notifications.show({ title: 'Error', message: 'No project ID', color: 'red' });
      return;
    }
    try {
      store.setLoading(true);
      setLoadingProgress('Clearing cache...');
      setError(null);
      await reloadScheduleFromServer({
        reason: 'invalidate-fetch',
        invalidateLocalCache: true,
      });
      setLoadingProgress('Fetching project locations');
      const tree = await useLocationsTreeStore.getState().fetchTreeIfNeeded(projectId);
      store.setLocationTree(tree);
      // Invalidate & Fetch establishes a new baseline from server data;
      // clear any previous session-level unsaved change tracking.
      store.clearSessionChanges();
      clearGanttSessionSnapshot(projectId);
      await refreshActiveRevision();
      notifications.show({ title: 'Done', message: 'Cache data removed & latest data fetched from server', color: 'teal' });
    } catch (err: any) {
      console.error('[ZustandGantt] Invalidate & Fetch failed:', err);
      setError(err?.message || 'Invalidate & Fetch failed');
      notifications.show({ title: 'Error', message: err?.message || 'Failed to refetch', color: 'red' });
    } finally {
      store.setLoading(false);
      setLoadingProgress('');
    }
  }, [projectId, store, reloadScheduleFromServer, refreshActiveRevision]);

  /**
   * Load tasks and location hierarchy on mount.
   * We load the location tree in the same flow so the grid can show location names
   * (locationId → name) without requiring the user to open the Location panel first.
   *
   * Readiness popover stays gated (`readinessDataReady` + calendar ready + not
   * loading) until the Gantt can show tasks — never expose assign/navigate CTAs
   * while the grid is still empty mid-load.
   */
  useEffect(() => {
    let cancelled = false;

    const waitUntilNotLoading = () =>
      new Promise<void>((resolve) => {
        if (!store.isLoading()) {
          resolve();
          return;
        }
        const unsub = store.subscribe(() => {
          if (!store.isLoading()) {
            unsub();
            resolve();
          }
        });
        // Safety: don't hang forever if loading flag sticks.
        window.setTimeout(() => {
          unsub();
          resolve();
        }, 120_000);
      });

    const loadTasks = async () => {
      if (!projectId) {
        setError('No project ID configured');
        return;
      }

      store.setLoading(true);
      setLoadingProgress('Loading schedule...');
      markGanttPerf('hydrate');
      markGanttPerf('firstPaint');

      // Start location tree fetch in parallel so we have id→name for the grid
      const treePromise = useLocationsTreeStore.getState().fetchTreeIfNeeded(projectId);

      try {
        const forceRefetch = consumeGanttForceRefetch(projectId);
        let hydrated = false;
        if (!forceRefetch) {
          // Try to hydrate from IDB first
          setLoadingProgress('Loading from local cache...');
          hydrated = await store.hydrate(projectId);
        }

        if (hydrated) {
          dismissGanttInlineEditing();
          const idbTasks = store.getTasks();

          // Empty-seed bug could leave only a location bootstrap root in IDB while the
          // server still has the real schedule — force a refetch in that case.
          if (isLikelyAutoSeedOnlySchedule(idbTasks)) {
            console.warn(
              '[ZustandGantt] IDB looks like auto-seed only; refetching schedule from server',
            );
            setLoadingProgress('Refreshing schedule from server...');
            await fetchFromApiAndPersist();
            const tree = await treePromise;
            if (!cancelled) store.setLocationTree(tree);
            measureGanttPerf('hydrate');
            deferAfterPaint(() => measureGanttPerf('firstPaint'));
            setLoadingProgress('');
            return;
          }

          setLoadingProgress('Loading location hierarchy...');

          const idbTaskIds = new Set<string>();
          const idbDuplicates: string[] = [];
          const idbUniqueProjectIds = new Set<string>();

          for (const t of idbTasks) {
            if (idbTaskIds.has(t.id)) {
              idbDuplicates.push(t.id);
            }
            idbTaskIds.add(t.id);

            if (t.projectId) {
              idbUniqueProjectIds.add(t.projectId);
            }
          }

          if (idbDuplicates.length > 0) {
            console.error(`[ZustandGantt] DUPLICATE TASKS IN IDB: ${idbDuplicates.length}`, idbDuplicates.slice(0, 10));
          }

          if (idbUniqueProjectIds.size > 1) {
            console.error(`[ZustandGantt] IDB contains tasks from MULTIPLE PROJECTS!`);
          }

          const tree = await treePromise;
          if (!cancelled) {
            store.setLocationTree(tree);
            const softLeaveSnapshot = loadGanttSessionSnapshot(projectId);
            if (softLeaveSnapshot) {
              store.restoreSessionSnapshot(softLeaveSnapshot);
              clearGanttSessionSnapshot(projectId);
            }
          }
          measureGanttPerf('hydrate');
          deferAfterPaint(() => measureGanttPerf('firstPaint'));
          setLoadingProgress('');
          return;
        }

        await fetchFromApiAndPersist();
        const tree = await treePromise;
        if (!cancelled) store.setLocationTree(tree);
        measureGanttPerf('hydrate');
        deferAfterPaint(() => measureGanttPerf('firstPaint'));
      } catch (err: any) {
        console.error('[ZustandGantt] Failed to load tasks:', err);
        setError(err?.message || 'Failed to load tasks');
      } finally {
        store.setLoading(false);
        setLoadingProgress('');
      }
    };

    setReadinessDataReady(false);

    void (async () => {
      try {
        if (!projectId) {
          setError('No project ID configured');
          return;
        }

        // A remount (e.g. Strict Mode) may leave a previous load in flight with empty tasks.
        // Wait for it — never treat "empty while loading" as a finished empty schedule.
        await waitUntilNotLoading();
        if (cancelled) return;

        if (store.getTasks().length === 0) {
          await loadTasks();
        } else if (!store.getLocationTree()?.length) {
          const tree = await useLocationsTreeStore.getState().fetchTreeIfNeeded(projectId);
          if (!cancelled) store.setLocationTree(tree);
        }
      } finally {
        if (!cancelled) setReadinessDataReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [store, projectId, fetchFromApiAndPersist, dismissGanttInlineEditing]);

  /** Once per project. Look ahead reads this cache; opening the view does not refetch. */
  useEffect(() => {
    if (!projectId) {
      setLookAheadConfig(fallbackLookAheadConfig());
      return;
    }
    let cancelled = false;
    setLookAheadConfig(fallbackLookAheadConfig());
    fetchLookAheadConfig(projectId)
      .then((next) => {
        if (cancelled) return;
        setLookAheadConfig({
          dayCount: next.dayCount,
          weekCount: next.weekCount,
          monthCount: next.monthCount,
          maxDaysFromToday: next.maxDaysFromToday ?? fallbackLookAheadConfig().maxDaysFromToday,
        });
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setLookAheadConfig(fallbackLookAheadConfig());
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return {
    lookAheadConfig,
    error,
    setError,
    loadingProgress,
    setLoadingProgress,
    readinessDataReady,
    ensureGanttRootAttemptedRef,
    fetchFromApiAndPersist,
    reloadScheduleFromServer,
    handleInvalidateAndFetch,
  };
}
